import React, { PropsWithChildren } from 'react';
import { graphql, useFragment } from 'react-relay';
import { matchPath, useLocation } from 'react-router-dom';
import { backofficeUrl } from 'src/shared/helpers';
import { TASKSTATUS } from './pages/salesChannels/switch/switchTypes';
import routes from 'src/shared/routes';
import { useAppState } from '../shared/appState';
import { appSubscriptionCheck$key } from './__generated__/appSubscriptionCheck.graphql';

export const appSubscriptionCheckFragment = graphql`
    fragment appSubscriptionCheck on Query {
        isAdminUser
        subscription {
            isCancelled
        }
        isSellerMigrating
        productSwitcherTasks {
            taskType
            state
            extraData {
                paymentMethodSetupTaskID
            }
        }
    }
`;

function _RequireSubscriptionCheck({ children, isSalesChannelsRoute }: PropsWithChildren<{ isSalesChannelsRoute: boolean }>) {
    const { queryData } = useAppState();

    const data = useFragment<appSubscriptionCheck$key>(appSubscriptionCheckFragment, queryData?.appSubscriptionCheck ?? null);
    const hasPendingPaymentMethodSetup = Boolean(
        data?.isSellerMigrating &&
        data.productSwitcherTasks?.some(
            (task) => task.taskType === 'EDITSUBSCRIPTION' && task.state === TASKSTATUS.Completed && task.extraData?.paymentMethodSetupTaskID != null
        )
    );

    if (data && data.isAdminUser) return null;

    if (!data?.subscription || data.subscription.isCancelled) {
        if (hasPendingPaymentMethodSetup && isSalesChannelsRoute) return <>{children}</>;

        const searchParams = new URLSearchParams(location.search);
        const error = searchParams.get('error');

        const redirectPath = hasPendingPaymentMethodSetup ? '/sales-channels' : '/seller/overviewsubscription';
        const redirectUrl = backofficeUrl(error ? `${redirectPath}?error=${encodeURIComponent(error)}` : redirectPath);

        window.location.href = redirectUrl;
        return null;
    }

    return <>{children}</>;
}

function AppSubscriptionCheck({ children }: PropsWithChildren<{}>) {
    const location = useLocation();
    const currentRoute = routes.find((x) => {
        const mathWithParams = matchPath(x.url.replaceAll('?', ''), location.pathname);

        // Remove all optioanl params from x.url
        const urlWithNoOptionalParams = x.url.replace(/:\w+\?/g, '');

        const matchNoParams = matchPath(urlWithNoOptionalParams, location.pathname + '/');

        return matchNoParams || mathWithParams;
    });
    const isSubscriptionRequested = currentRoute?.isSubscriptionRequested ?? true;
    const isSalesChannelsRoute = currentRoute?.url === backofficeUrl('/sales-channels');

    if (!isSubscriptionRequested) {
        return children;
    }

    return <_RequireSubscriptionCheck isSalesChannelsRoute={isSalesChannelsRoute}>{children}</_RequireSubscriptionCheck>;
}

export default AppSubscriptionCheck;
